<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
// NOTICE: KAPTION-INTEGRATION - downloadFile added
import { downloadFile, getFileInfo } from '@chatwoot/utils';
import { useAlert } from 'dashboard/composables';

import FileIcon from 'next/icon/FileIcon.vue';
import Icon from 'next/icon/Icon.vue';
// NOTICE: KAPTION-INTEGRATION - pdf-preview.CHIP.1
import KaptionPdfPreview from 'next/message/kaption/KaptionPdfPreview.vue';

const { attachment } = defineProps({
  attachment: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();

const fileDetails = computed(() => {
  return getFileInfo(attachment?.dataUrl || '');
});

// NOTICE: KAPTION-INTEGRATION
// Force a named download instead of <a target="_blank"> — the Active Storage
// proxy serves documents with Content-Disposition: inline, which opened PDFs
// in a tab instead of saving them (see bubbles/File.vue).
const downloadAttachment = async () => {
  try {
    await downloadFile({
      url: attachment.dataUrl,
      type: attachment.fileType || 'file',
      extension: attachment.extension || fileDetails.value.type,
    });
  } catch (error) {
    useAlert(t('GALLERY_VIEW.ERROR_DOWNLOADING'));
  }
};

// NOTICE: KAPTION-INTEGRATION
// pdf-preview.CHIP.1-3 — most PDFs arrive with a caption, which renders the
// document as a chip inside the text bubble rather than as a file bubble. Both
// places open the same viewer, so reading an invoice never means a trip through
// the downloads folder.
const showPreview = ref(false);

const isPdf = computed(() => {
  const extension = attachment?.extension || fileDetails.value.type || '';
  return extension.toLowerCase() === 'pdf';
});

const openPreview = () => {
  showPreview.value = true;
};

const displayFileName = computed(() => {
  const { base, type } = fileDetails.value;
  const truncatedName = (str, maxLength, hasExt) =>
    str.length > maxLength
      ? `${str.substring(0, maxLength).trimEnd()}${hasExt ? '..' : '...'}`
      : str;

  return type
    ? `${truncatedName(base, 12, true)}.${type}`
    : truncatedName(base, 14, false);
});

const textColorClass = computed(() => {
  const colorMap = {
    '7z': 'dark:text-[#EDEEF0] text-[#2F265F]',
    csv: 'text-n-amber-12',
    doc: 'dark:text-[#D6E1FF] text-[#1F2D5C]', // indigo-12
    docx: 'dark:text-[#D6E1FF] text-[#1F2D5C]', // indigo-12
    json: 'text-n-slate-12',
    odt: 'dark:text-[#D6E1FF] text-[#1F2D5C]', // indigo-12
    pdf: 'text-n-slate-12',
    ppt: 'dark:text-[#FFE0C2] text-[#582D1D]',
    pptx: 'dark:text-[#FFE0C2] text-[#582D1D]',
    rar: 'dark:text-[#EDEEF0] text-[#2F265F]',
    rtf: 'dark:text-[#D6E1FF] text-[#1F2D5C]', // indigo-12
    tar: 'dark:text-[#EDEEF0] text-[#2F265F]',
    txt: 'text-n-slate-12',
    xls: 'text-n-teal-12',
    xlsx: 'text-n-teal-12',
    zip: 'dark:text-[#EDEEF0] text-[#2F265F]',
  };

  return colorMap[fileDetails.value.type] || 'text-n-slate-12';
});
</script>

<template>
  <div
    class="h-9 bg-n-alpha-white gap-2 overflow-hidden items-center flex px-2 rounded-lg border border-n-container"
  >
    <FileIcon class="flex-shrink-0" :file-type="fileDetails.type" />
    <!-- NOTICE: KAPTION-INTEGRATION - pdf-preview.CHIP.1: the name opens a PDF -->
    <button
      v-if="isPdf"
      type="button"
      data-testid="kaption-chip-name"
      class="flex-1 min-w-0 text-sm text-left truncate cursor-pointer max-w-36 hover:underline"
      :title="fileDetails.name"
      :class="textColorClass"
      @click="openPreview"
    >
      {{ displayFileName }}
    </button>
    <span
      v-else
      class="flex-1 min-w-0 text-sm max-w-36"
      :title="fileDetails.name"
      :class="textColorClass"
    >
      {{ displayFileName }}
    </span>
    <!-- NOTICE: KAPTION-INTEGRATION - pdf-preview.CHIP.2 -->
    <button
      v-if="isPdf"
      v-tooltip="t('KAPTION_FILE_PREVIEW.VIEW')"
      type="button"
      data-testid="kaption-chip-view"
      :aria-label="t('KAPTION_FILE_PREVIEW.VIEW')"
      class="grid transition-colors cursor-pointer flex-shrink-0 size-9 place-content-center text-n-slate-11 hover:text-n-slate-12"
      @click="openPreview"
    >
      <Icon icon="i-lucide-eye" />
    </button>
    <!-- NOTICE: KAPTION-INTEGRATION - button + forced download (was <a target="_blank">) -->
    <button
      v-tooltip="t('CONVERSATION.DOWNLOAD')"
      type="button"
      :aria-label="t('CONVERSATION.DOWNLOAD')"
      class="flex-shrink-0 size-9 grid place-content-center cursor-pointer text-n-slate-11 hover:text-n-slate-12 transition-colors"
      @click="downloadAttachment"
    >
      <Icon icon="i-lucide-download" />
    </button>
  </div>
  <!-- NOTICE: KAPTION-INTEGRATION - pdf-preview.CHIP.3 -->
  <KaptionPdfPreview
    v-if="showPreview"
    :url="attachment.dataUrl"
    :name="fileDetails.name"
    :extension="attachment.extension || fileDetails.type"
    @close="showPreview = false"
  />
</template>
